<script setup lang="ts">
import {computed} from 'vue'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {IconWeightType} from '../enum/IconWeightType'
import {IconWeightTypeToClassNameMap} from '../maps/IconWeightTypeToClassNameMap'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'
import {SizeVariantType} from '@toolify/client/src/modules/ui/enum/SizeVariantType'
import {IconBehavior} from '../enum/IconBehavior'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'

interface Props {
  name: IconNameType
  behavior?: IconBehavior
  weightType?: IconWeightType
  styleVariant?: StyleVariantType
  colorVariant?: ColorVariantType
  size?: SizeVariantType
}

const props = withDefaults(defineProps<Props>(), {
  styleVariant: StyleVariantType.ClearFilled,
  colorVariant: ColorVariantType.Neutral,
  size: SizeVariantType.Standard,
  behavior: IconBehavior.None,
})

const {getThemeVariable} = useThemeVariables()
const resolvedWeightType = computed(() => {
  return props.weightType ?? getThemeVariable.value(ThemeVariableType.PreferredIconWeightType)
})
</script>

<template>
  <div
    class="icon-button"
    :class="[`theme-${props.colorVariant}`, `style-${props.styleVariant}`, `wrapper-size-${props.size}`]"
  >
    <FontAwesomeIcon
      :icon="[IconWeightTypeToClassNameMap.get(resolvedWeightType), props.name]"
      :spin="props.behavior === IconBehavior.Spin"
      class="icon"
    />
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;

.icon-button {
  display: flex;
  justify-content: center;
  align-items: center;

  &.wrapper-size-large{
    font-size: map-get(variables.$iconSizes, large);
    width: map-get(variables.$componentSizes, large);
    height: map-get(variables.$componentSizes, large);
    border-radius: map-get(variables.$componentRadius, large);
  }

  &.wrapper-size-standard{
    font-size: map-get(variables.$iconSizes, standard);
    width: map-get(variables.$componentSizes, standard);
    height: map-get(variables.$componentSizes, standard);
    border-radius: map-get(variables.$componentRadius, standard);
  }

  &.wrapper-size-medium{
    font-size: map-get(variables.$iconSizes, medium);
    width: map-get(variables.$componentSizes, medium);
    height: map-get(variables.$componentSizes, medium);
    border-radius: map-get(variables.$componentRadius, medium);
  }

  &.wrapper-size-small{
    font-size: map-get(variables.$iconSizes, small);
    width: map-get(variables.$componentSizes, small);
    height: map-get(variables.$componentSizes, small);
    border-radius: map-get(variables.$componentRadius, small);
  }

  &:hover{
    cursor: pointer;
  }
}

.theme-error{
  color: map-get(variables.$colors, content-dark);

  &:hover{
    color: map-get(variables.$colors, on-error-container);
  }

  &.style-clear-filled:hover{
    color: map-get(variables.$colors, error);
    background-color: map-get(variables.$colors, container-high);
  }

  &.style-filled-filled{
    color: map-get(variables.$colors, error);
    background: map-get(variables.$colors, container);

    &:hover{
      background: map-get(variables.$colors, container-high);
    }
  }
}

.theme-neutral{
  color: map-get(variables.$colors, content-dim);

  &:hover{
    color: map-get(variables.$colors, content-normal);
  }

  &.style-clear-filled:hover{
    background-color: map-get(variables.$colors, container-high);
  }

  &.style-filled-filled{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container-high);

    &:hover{
      background: map-get(variables.$colors, container-highest);
    }
  }
}

.theme-primary{
  color: map-get(variables.$colors, on-primary);

  &:hover{
    color: map-get(variables.$colors, on-primary-hover);
  }

  &.style-clear-filled:hover{
    color: map-get(variables.$colors, on-primary-hover);
    background: map-get(variables.$colors, primary);
  }

  &.style-filled-filled{
    color: map-get(variables.$colors, on-primary);
    background: map-get(variables.$colors, primary);

    &:hover{
      background: map-get(variables.$colors, primary-hover);
    }
  }
}

.theme-dark{
  color: map-get(variables.$colors, content-dark);

  &:hover{
    color: map-get(variables.$colors, content-normal);
  }

  &.style-clear-filled:hover{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container);
  }

  &.style-filled-filled{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container);

    &:hover{
      background: map-get(variables.$colors, container-high);
    }
  }
}

.theme-dim{
  color: map-get(variables.$colors, content-dim);

  &:hover{
    color: map-get(variables.$colors, content-normal);
  }

  &.style-clear-filled:hover{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container);
  }

  &.style-filled-filled{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container);

    &:hover{
      background: map-get(variables.$colors, container-high);
    }
  }
}

.theme-darkest{
  color: map-get(variables.$colors, content-darkest);

  &:hover{
    color: map-get(variables.$colors, content-dim);
  }

  &.style-clear-filled:hover{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container-low);
  }

  &.style-filled-filled{
    color: map-get(variables.$colors, content-normal);
    background: map-get(variables.$colors, container-low);

    &:hover{
      background: map-get(variables.$colors, container);
    }
  }
}
</style>
